"use client";

import { useMemo, useRef } from "react";
import { motion } from "motion/react";
import DottedMap from "dotted-map";
import { cn } from "@/lib/utils";

export type WorldMapDot = {
  start: { lat: number; lng: number; label?: string };
  end: { lat: number; lng: number; label?: string };
};

interface MapProps {
  dots?: WorldMapDot[];
  lineColor?: string;
  className?: string;
  /** Dot fill for the base map (default: translucent white for dark UIs). */
  mapDotColor?: string;
  /**
   * Gate route / pin motion until the section is on screen.
   * When false, paths stay at pathLength 0 and pulses stay off.
   */
  active?: boolean;
  /** Extra delay (seconds) before the first route draws once `active`. */
  animationDelay?: number;
}

/** WGS84 Web Mercator radius (matches `dotted-map` default `+proj=merc`). */
const MERCATOR_R = 6378137;

/** Bounds dotted-map exposes at runtime but marks private in typings. */
type DottedMapBounds = {
  X_MIN: number;
  Y_MAX: number;
  X_RANGE: number;
  Y_RANGE: number;
  width: number;
  height: number;
};

export default function WorldMap({
  dots = [],
  lineColor = "#ffffff",
  className,
  mapDotColor = "#FFFFFF40",
  active = true,
  animationDelay = 0,
}: MapProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  const { svgMap, width, height, projectPoint } = useMemo(() => {
    const map = new DottedMap({ height: 100, grid: "diagonal" });
    const bounds = map as unknown as DottedMapBounds;
    const svg = map.getSVG({
      radius: 0.22,
      color: mapDotColor,
      shape: "circle",
      backgroundColor: "transparent",
    });

    /**
     * Project WGS84 lat/lng into dotted-map Mercator space.
     * Aceternity’s equirectangular 800×400 overlay does not match the Mercator
     * dotted grid — pins landed in the wrong countries.
     */
    const project = (lat: number, lng: number) => {
      const projX = MERCATOR_R * ((lng * Math.PI) / 180);
      const projY =
        MERCATOR_R *
        Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
      /* Stable 2-dp strings — raw floats differ between Node and the browser. */
      return {
        x: Math.round((((projX - bounds.X_MIN) / bounds.X_RANGE) * bounds.width) * 100) / 100,
        y: Math.round((((bounds.Y_MAX - projY) / bounds.Y_RANGE) * bounds.height) * 100) / 100,
      };
    };

    return {
      svgMap: svg,
      width: bounds.width,
      height: bounds.height,
      projectPoint: project,
    };
  }, [mapDotColor]);

  const createCurvedPath = (
    start: { x: number; y: number },
    end: { x: number; y: number },
  ) => {
    const midX = Math.round(((start.x + end.x) / 2) * 100) / 100;
    /* Arc height scales with map height (was −50 on a 400-tall viewBox). */
    const midY = Math.round((Math.min(start.y, end.y) - height * 0.125) * 100) / 100;
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  return (
    <div
      className={cn(
        "relative aspect-[2/1] w-full bg-transparent font-sans",
        className,
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
        className="pointer-events-none h-full w-full select-none [mask-image:linear-gradient(to_bottom,transparent,white_10%,white_90%,transparent)]"
        alt="world map"
        height={height}
        width={width}
        draggable={false}
      />
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="pointer-events-none absolute inset-0 h-full w-full select-none"
        aria-hidden
      >
        {dots.map((dot, i) => {
          const startPoint = projectPoint(dot.start.lat, dot.start.lng);
          const endPoint = projectPoint(dot.end.lat, dot.end.lng);
          return (
            <g key={`path-group-${i}`}>
              <motion.path
                d={createCurvedPath(startPoint, endPoint)}
                fill="none"
                stroke="url(#path-gradient)"
                strokeWidth="0.35"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: active ? 1 : 0 }}
                transition={{
                  duration: 1,
                  delay: active ? animationDelay + 0.45 * i : 0,
                  ease: "easeOut",
                }}
              />
            </g>
          );
        })}

        <defs>
          <linearGradient id="path-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="5%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="95%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>

        {active
          ? dots.map((dot, i) => (
              <g key={`points-group-${i}`}>
                <g key={`start-${i}`}>
                  <circle
                    cx={projectPoint(dot.start.lat, dot.start.lng).x}
                    cy={projectPoint(dot.start.lat, dot.start.lng).y}
                    r="0.55"
                    fill={lineColor}
                  />
                  <circle
                    cx={projectPoint(dot.start.lat, dot.start.lng).x}
                    cy={projectPoint(dot.start.lat, dot.start.lng).y}
                    r="0.55"
                    fill={lineColor}
                    opacity="0.5"
                  >
                    <animate
                      attributeName="r"
                      from="0.55"
                      to="2.2"
                      dur="1.5s"
                      begin={`${animationDelay + 0.2}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      from="0.5"
                      to="0"
                      dur="1.5s"
                      begin={`${animationDelay + 0.2}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
                <g key={`end-${i}`}>
                  <circle
                    cx={projectPoint(dot.end.lat, dot.end.lng).x}
                    cy={projectPoint(dot.end.lat, dot.end.lng).y}
                    r="0.55"
                    fill={lineColor}
                  />
                  <circle
                    cx={projectPoint(dot.end.lat, dot.end.lng).x}
                    cy={projectPoint(dot.end.lat, dot.end.lng).y}
                    r="0.55"
                    fill={lineColor}
                    opacity="0.5"
                  >
                    <animate
                      attributeName="r"
                      from="0.55"
                      to="2.2"
                      dur="1.5s"
                      begin={`${animationDelay + 0.35 + 0.12 * i}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      from="0.5"
                      to="0"
                      dur="1.5s"
                      begin={`${animationDelay + 0.35 + 0.12 * i}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              </g>
            ))
          : null}
      </svg>
    </div>
  );
}
